import { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { isEqual } from 'lodash';
import { ORDER_TYPES } from 'shared/types/cart/Cart';
import { LineItem } from 'shared/types/cart/LineItem';
import { Availability } from 'shared/types/product/InventoryQuery';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import P65Warning from '@/components/customercenter-ui/ui/p65Warning';
import {
  daysBetweenCurrentAndDate,
  getArrivalAndBackorderDateFromResponse,
  isFutureDate,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { CartHelpers } from '@/helpers/utils/cartHelpers';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import {
  getShippingBackorderDateFromAvailability,
  hasCompleteAvailabilityData,
  mapLineItemsToAvailability,
} from '@/helpers/utils/mapLineItemsAvailability';
import { PathNames, extractCustomerId, TranslateText, getSelectedSite } from '@/helpers/utils/siteUtils';
import { COOKIE_KEYS, DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import { AvailabilityMsgDisplay } from '../../../product/details/components/productDetails/availabilityMsg';
import Tags from '../../../tags';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { PageTexts } from '../../i18n';
import CustomerOrderSummary from '../OrderSummary/orderSummary';

export const isPersonalizedItem = (lineItem: any): boolean => {
  return !!(lineItem?.variant?.attributes?.personalizeOption && lineItem?.personalizeProductId);
};

export const getPersonalizedText = (lineItem: any): string => {
  let personalizetext = lineItem?.personalizeText ?? '';
  const result: string[] = [];

  const extractTextValues = (items?: any[]): void => {
    items?.forEach((item) => {
      if (item?.text) {
        result.push(item?.text);
      }
    });
  };

  const extractSideValues = (side: any): void => {
    side?.text && extractTextValues(side?.text);
    side?.circletext && extractTextValues(side?.circletext);
  };

  try {
    const personalizeJson = JSON.parse(personalizetext);
    if (personalizeJson) {
      personalizeJson?.Front && extractSideValues(personalizeJson?.Front);
      personalizeJson?.Back && extractSideValues(personalizeJson?.Back);
      personalizeJson?.FRONT && extractSideValues(personalizeJson?.FRONT);
      personalizeJson?.BACK && extractSideValues(personalizeJson?.BACK);
      personalizetext = result?.join(', ');
    }
  } catch (error) {}
  return personalizetext;
};

const CustomerCartDetails = ({ showOrderSummary = true, currentCheckoutStep, pageContext }: any) => {
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const { customerData } = useContext(CreditSummaryContext);
  const [showCart, setShowCart] = useState(false);
  const [defaultOpenValue, setDefaultOpenValue] = useState('closed');
  const isCheckoutPage = pageContext === 'checkout' || currentCheckoutStep !== undefined;
  const {
    contextCart,
    showShippingNotApplicableError,
    selectedShippingMethod,
    cartDeliveryDate,
    lineItemsAvailibiltyMsgs,
    setShippingBackorderDate,
    setLineItemsAvailibiltyMsgs,
    deliveryStatus,
    notificationMessage,
    isError,
    shippingAddress,
  } = useCheckout();
  const { getLineItemsAvailibilty, isLoading, data: swrCart } = useCart(false);
  const data = isCheckoutPage ? contextCart ?? swrCart : swrCart;
  const router = useRouter();
  const [isShippingToCalifornia, setIsShippingToCalifornia] = useState(false);
  const [currentShippingState, setCurrentShippingState] = useState(data?.shippingAddress?.state);
  const projectSettings: ProjectSettings = useProjSettings();
  const showActiveBrandSection = useShowActiveBrandSection();
  const siteId = getSelectedSite() ?? 'ZBSite';

  useEffect(() => {
    const isShippingToCalifornia = data?.shippingAddress?.state === mapLocaleToMeaningfulFormat().california;
    setIsShippingToCalifornia(isShippingToCalifornia);
    const isProp65ChemicalPresent = data?.lineItems?.some(
      (cartItem: any) => cartItem?.variant?.attributes?.prop65ChemicalPresent === 'Yes',
    );

    if (currentCheckoutStep === 3 && isShippingToCalifornia && isProp65ChemicalPresent) {
      setDefaultOpenValue('item-1');
    }
  }, [data]);

  useEffect(() => {
    const currentCustomerId = data?.additionalDetails?.omsCustomerId ?? customerData?.CustomerInfo?.omsCustomerId;
    const customerCookieData = getCookie(COOKIE_KEYS.URL_HISTORY);
    if (customerCookieData) {
      const currentCustomerURL = JSON.parse(customerCookieData);
      const CustomerLink = currentCustomerURL[PathNames.CUSTOMER_DETAILS];
      if (CustomerLink?.link) {
        const customerId = extractCustomerId(CustomerLink?.link) ?? '';
        if (customerId === currentCustomerId) {
          setShowCart(true);
        } else {
          setShowCart(false);
        }
      }
    }
  }, [data, customerData]);

  useEffect(() => {
    if (shippingAddress) {
      setCurrentShippingState(shippingAddress?.state);
    }
  }, [shippingAddress]);

  useEffect(() => {
    if (!deliveryStatus && (data?.containsPOBoxItem || data?.stateNotAvail)) {
      setDefaultOpenValue('item-1');
    }
  }, [deliveryStatus]);

  const applyAvailabilityResult = (result: Availability[]) => {
    const backOrderDate = getShippingBackorderDateFromAvailability(result);
    setShippingBackorderDate(backOrderDate);
    if (!isEqual(result, lineItemsAvailibiltyMsgs)) {
      setLineItemsAvailibiltyMsgs(result);
    }
  };

  const fetchLineItemsAvailibilty = async () => {
    try {
      const result: Availability[] = await getLineItemsAvailibilty();
      applyAvailabilityResult(result ?? []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  useEffect(() => {
    if (isLoading || !data?.lineItems || data?.lineItems?.length === 0) {
      return;
    }

    if (isCheckoutPage) {
      if (hasCompleteAvailabilityData(data.lineItems)) {
        applyAvailabilityResult(mapLineItemsToAvailability(data.lineItems));
        return;
      }
      fetchLineItemsAvailibilty();
      return;
    }

    fetchLineItemsAvailibilty();
  }, [isLoading, isCheckoutPage]);

  const getAvailibityForEachLineItem = (lineItem: LineItem) => {
    const availabilityMsg = lineItemsAvailibiltyMsgs?.find(
      (item: Availability) => item?.sku === lineItem?.variant?.sku,
    );
    return availabilityMsg as Availability;
  };

  return (
    showActiveBrandSection && (
      <>
        <Accordion
          type="single"
          collapsible
          className="w-full rounded-md border border-foggyGray px-5 pb-7 pt-6 shadow-lg"
          value={defaultOpenValue}
          onValueChange={setDefaultOpenValue}
        >
          <AccordionItem value="item-1" className="border-none">
            <AccordionTrigger className="m-0 p-0 text-lg font-bold hover:no-underline">
              {showCart ? (
                <div>
                  {data?.lineItems?.filter((item) => !item.isInsuranceItem).length ?? 0} {Texts.itemsInCart}
                </div>
              ) : (
                <div>{Texts.noItemsInCart}</div>
              )}
            </AccordionTrigger>
            <AccordionContent>
              {showCart && (
                <>
                  <button
                    className={`text-blueLight underline ${data?.lineItems?.length === 0 ? 'hidden' : 'block'}`}
                    onClick={() => router.push('/cart')}
                  >
                    {Texts.goToCart}
                  </button>
                  <section className="mt-4 flex flex-col">
                    {data?.lineItems
                      ?.filter((lineItem: LineItem) => !lineItem.isInsuranceItem)
                      .map((lineItem: LineItem, index: number) => {
                        const isLastItem = index === (data?.lineItems?.length ?? 0) - 1;
                        const isMailOrderProduct = lineItem?.variant?.attributes?.abwStyle === '999999';
                        const isFreeGiftItem: boolean = lineItem?.isGift ?? false;
                        const isNoChargeOrder = data?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE;
                        let showFreeText = false;
                        const totalLineItemPrice = lineItem?.totalPrice;
                        if (isFreeGiftItem || totalLineItemPrice?.centAmount === 0) {
                          showFreeText = true;
                        }
                        let personalizeText = null;
                        let isPersonalized = false;
                        let parentItem = null;
                        isPersonalized = isPersonalizedItem(lineItem);
                        if (isPersonalized) {
                          personalizeText = getPersonalizedText(lineItem);
                        }
                        if (lineItem?.lineGroupId) {
                          parentItem = data?.lineItems?.find((parentLineItem: LineItem) => {
                            return parentLineItem.lineItemId === lineItem?.lineGroupId;
                          });
                          if (parentItem) {
                            isPersonalized = isPersonalizedItem(parentItem);
                          }
                        }
                        const stateNotAvailForLineItem = lineItem?.variant?.attributes?.stateNotAvail?.find(
                          (state: string) =>
                            state?.trim()?.toLowerCase() === currentShippingState?.trim()?.toLowerCase(),
                        );
                        const shippingMethodErrorFlag =
                          (lineItem?.variant?.attributes?.priorityShipAvail === false &&
                            selectedShippingMethod?.displayName?.toLowerCase() ===
                              mapLocaleToMeaningfulFormat().priorityShippingMethod) ||
                          (lineItem?.variant?.attributes?.expressShipAvail === false &&
                            selectedShippingMethod?.displayName?.toLowerCase() ===
                              mapLocaleToMeaningfulFormat().expressShippingMethod);
                        const showShippingError = showShippingNotApplicableError && shippingMethodErrorFlag;
                        const valueToClassName: any = {
                          onlyLeft: 'text-stoneberry-onlyLeft',
                          backOrdered: 'text-stoneberry-backordered',
                          inStock: 'text-forestGreen',
                        };
                        const orderDiscountShare = lineItem?.orderDiscountShare;
                        const itemLevelDiscount = lineItem?.itemLevelDiscount;
                        const totalItemLevelDiscount = CartHelpers.calculateTotalLineDiscount(
                          orderDiscountShare,
                          itemLevelDiscount,
                        );
                        const totalItemLevelDiscountGreaterThanZero = (totalItemLevelDiscount?.centAmount ?? 0) > 0;
                        const availabilityData = getAvailibityForEachLineItem(lineItem);
                        const availabilityClassName =
                          valueToClassName[availabilityData?.value as string] || 'text-[#DF2429]';
                        const isOnBackOrder = availabilityData?.value === 'backOrdered';
                        const isInStockWithBackOrder = (isOnBackOrder && lineItem?.variant?.isOnStock) ?? false;
                        let cartItemBackorderDate = '';
                        let standardBackorderDate = '';
                        const dropShipValue = lineItem?.variant?.attributes?.dropShipEligible || false;
                        let intervalDays = 0;
                        if (
                          availabilityData?.backOrderDateWithYear &&
                          isFutureDate(availabilityData?.backOrderDateWithYear)
                        ) {
                          intervalDays = daysBetweenCurrentAndDate(availabilityData?.backOrderDateWithYear);
                        }
                        let cartItemDeliveryDate = '';
                        let cartItemStandardDeliveryDate = '';
                        if (cartDeliveryDate) {
                          const cartDeliveryDateJSON = JSON.parse(cartDeliveryDate);
                          if (cartDeliveryDateJSON?.actualArrivalDate) {
                            const { arrivalDate, backorderDate } = getArrivalAndBackorderDateFromResponse(
                              cartDeliveryDateJSON?.actualArrivalDate,
                              dropShipValue,
                              isPersonalized,
                              projectSettings,
                              false,
                              intervalDays,
                            );
                            cartItemDeliveryDate = isInStockWithBackOrder ? arrivalDate : backorderDate;
                            cartItemBackorderDate = backorderDate;
                            if (cartDeliveryDateJSON?.standardDeliveryDate) {
                              const { arrivalDate: standardArrivalDate, backorderDate: standardBackOrderDate } =
                                getArrivalAndBackorderDateFromResponse(
                                  cartDeliveryDateJSON?.standardDeliveryDate,
                                  dropShipValue,
                                  isPersonalized,
                                  projectSettings,
                                  false,
                                  intervalDays,
                                );
                              cartItemStandardDeliveryDate = isInStockWithBackOrder
                                ? standardArrivalDate
                                : standardBackOrderDate;
                              standardBackorderDate = standardBackOrderDate;
                            }
                          }
                        }
                        return (
                          <section
                            key={lineItem?.lineItemId}
                            className={`py-2 ${!isLastItem ? 'border-b border-foggyGray' : ''}`}
                          >
                            <section className="flex gap-2">
                              <section className="w-2/5">
                                {lineItem?.variant?.attributes?.mainImageURL ? (
                                  <Link
                                    prefetch={false}
                                    href={lineItem?._url || '#'}
                                    passHref
                                    title={lineItem?.name ?? ''}
                                  >
                                    <ContentStackImage
                                      className="w-full"
                                      src={lineItem?.variant?.attributes?.mainImageURL}
                                      imageWidth={120}
                                      imageHeight={120}
                                      alt={`Product Image ${lineItem?.lineItemId}`}
                                      loading="eager"
                                    />
                                  </Link>
                                ) : (
                                  <NoImage width={'full'} height={120} />
                                )}
                              </section>
                              <section className="flex w-3/5 flex-col gap-2">
                                <Link prefetch={false} href={lineItem?._url || '#'} passHref>
                                  <div
                                    className="font-semibold hover:underline"
                                    dangerouslySetInnerHTML={{ __html: lineItem?.name || '' }}
                                  />
                                </Link>
                                <div>
                                  {Texts.itemNumber}: {lineItem?.variant?.attributes?.abwStyle}
                                </div>
                                <div>
                                  <div>
                                    {lineItem?.variant?.attributes?.variantSelectorValue1} |{' '}
                                    {lineItem?.variant?.attributes?.variantSelectorValue2}
                                    {lineItem?.variant?.attributes?.variantSelectorValue3}
                                  </div>
                                </div>
                                {personalizeText && (
                                  <section className="mt-4 flex w-full gap-2 rounded-sm border border-foggyGray bg-lightSilver p-3 focus:outline-none ">
                                    <p className="text-sm font-normal leading-[19.4px] text-black">
                                      <strong>{mapLocaleToMeaningfulFormat().personalization}:</strong>{' '}
                                      <div className="text-wrap gbmask break-all">{personalizeText}</div>
                                    </p>
                                  </section>
                                )}
                                {lineItem?.listPrice?.centAmount === lineItem?.price?.centAmount ||
                                isMailOrderProduct ? (
                                  <p className="mt-2 text-lg font-bold">
                                    {CurrencyHelpers.customCurrencyFormat(lineItem?.price ?? { centAmount: 0 })}
                                  </p>
                                ) : showFreeText && !isNoChargeOrder ? (
                                  <p className="mt-2 flex gap-2 text-lg font-bold">
                                    <strong className={`${clearanceTextClass(siteId)}`}>{`${
                                      mapLocaleToMeaningfulFormat().free
                                    } `}</strong>
                                    <span className="line-through">
                                      {CurrencyHelpers.customCurrencyFormat(lineItem?.price ?? { centAmount: 0 })}
                                    </span>
                                  </p>
                                ) : (
                                  <div className="mt-2">
                                    {lineItem?.activePriceType && (
                                      <Tags
                                        activePriceType={lineItem?.activePriceType}
                                        overrideTagLabel={lineItem?.priceLabel}
                                      />
                                    )}
                                    {lineItem?.price?.centAmount === lineItem?.lastPrice?.centAmount ? (
                                      lineItem?.listPrice ? (
                                        <p className="flex gap-2 text-lg">
                                          <span className={`font-bold ${clearanceTextClass(siteId)}`}>
                                            {CurrencyHelpers.customCurrencyFormat(lineItem?.price ?? { centAmount: 0 })}
                                          </span>
                                          <span className="text-[#666666] line-through">
                                            {CurrencyHelpers.customCurrencyFormat(
                                              lineItem?.listPrice ?? { centAmount: 0 },
                                            )}
                                          </span>
                                        </p>
                                      ) : (
                                        <p className="mt-2 text-lg font-bold">
                                          {CurrencyHelpers.customCurrencyFormat(lineItem?.price ?? { centAmount: 0 })}
                                        </p>
                                      )
                                    ) : (
                                      <p className="flex gap-2 text-lg">
                                        <span className={`font-bold ${clearanceTextClass(siteId)}`}>
                                          {CurrencyHelpers.customCurrencyFormat(lineItem?.price ?? { centAmount: 0 })}
                                        </span>
                                        <span className="text-[#666666] line-through">
                                          {CurrencyHelpers.customCurrencyFormat(
                                            lineItem?.lastPrice ?? { centAmount: 0 },
                                          )}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                )}
                                {totalItemLevelDiscountGreaterThanZero && !showFreeText && (
                                  <p className="text-[13px] leading-[20.8pxpx] text-black">
                                    <span>{`${mapLocaleToMeaningfulFormat().discount}:`}</span>{' '}
                                    <strong className={`${clearanceTextClass(siteId)}`}>
                                      {CurrencyHelpers.formatForCurrency(totalItemLevelDiscount, true)}
                                    </strong>
                                  </p>
                                )}
                              </section>
                            </section>
                            <section className="pb-4">
                              {availabilityData?.message && (
                                <AvailabilityMsgDisplay
                                  isInStockWithBackOrder={isInStockWithBackOrder}
                                  availabilityMsg={availabilityData?.message}
                                  arrivalDate={
                                    showShippingError ? cartItemStandardDeliveryDate : cartItemDeliveryDate ?? ''
                                  }
                                  backOrderedArrivalDate={
                                    showShippingError ? standardBackorderDate : cartItemBackorderDate
                                  }
                                  availabilityClassName={availabilityClassName}
                                  isCheckout={true}
                                  qty={lineItem?.count}
                                  stockValue={availabilityData?.stockValue}
                                />
                              )}
                            </section>
                            <section>
                              {currentCheckoutStep === 3 &&
                                isShippingToCalifornia &&
                                lineItem?.variant?.attributes?.prop65ChemicalPresent === 'Yes' && (
                                  <div className="mb-4 mr-1 mt-2 flex gap-2">
                                    <P65Warning />
                                    <p>
                                      <span className="font-bold">{mapLocaleToMeaningfulFormat().p65Warning}</span>
                                      {mapLocaleToMeaningfulFormat().p65Text}
                                      <a
                                        href={mapLocaleToMeaningfulFormat().p65Link}
                                        target="__blank"
                                        className="ml-1 underline"
                                      >
                                        {mapLocaleToMeaningfulFormat().p65LinkText}
                                      </a>
                                    </p>
                                  </div>
                                )}
                              {showShippingError && selectedShippingMethod?.displayName && (
                                <p className="pb-6 pt-2">
                                  <strong className="text-scarletRed">
                                    <strong>
                                      {mapLocaleToMeaningfulFormat().cartItemShippingMethodNotApplicableError}
                                    </strong>
                                  </strong>
                                </p>
                              )}
                              {!deliveryStatus &&
                                isError &&
                                stateNotAvailForLineItem &&
                                notificationMessage ===
                                  mapLocaleToMeaningfulFormat([`${currentShippingState}`])
                                    .stateNotAvailPageLevelText && (
                                  <h1 className="pb-6 text-base font-semibold text-scarletRed">
                                    {
                                      mapLocaleToMeaningfulFormat([`${currentShippingState}`])
                                        .stateNotAvailItemLevelText
                                    }
                                  </h1>
                                )}
                              {!deliveryStatus &&
                                isError &&
                                !lineItem?.variant?.attributes?.poBoxAvail &&
                                notificationMessage === mapLocaleToMeaningfulFormat().deliveryError && (
                                  <h1 className="pb-6 text-base font-semibold text-scarletRed">
                                    {mapLocaleToMeaningfulFormat().cartAccordionDeliveryError}
                                  </h1>
                                )}
                            </section>
                          </section>
                        );
                      })}
                  </section>
                  <section>{data?.lineItems?.length === 0 && Texts.noItemsInCart}</section>
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {showOrderSummary && <CustomerOrderSummary showButton={showCart} data={data} />}
      </>
    )
  );
};

export default CustomerCartDetails;
